import { inject } from "@angular/core";
import { AccountBackend } from "src/app/backend/api/account.backend";
import { AlarmBackend } from "src/app/backend/api/alarm.backend";
import { AppointmentBackend } from "src/app/backend/api/appointment.backend";
import { AppointmentTemplateBackend } from "src/app/backend/api/appointment-template.backend";
import { AttachmentBackend } from "src/app/backend/api/attachment.backend";
import { AttachmentBillingBackend } from "src/app/backend/api/attachment-billing.backend";
import { AttachmentDirectoryBackend } from "src/app/backend/api/attachment-directory.backend";
import { BankAccountBackend } from "src/app/backend/api/bank-account.backend";
import { CampaignBackend } from "src/app/backend/api/campaign.backend";
import { CartBackend } from "src/app/backend/api/cart.backend";
import { ConfigurationBackend } from "src/app/backend/api/configuration.backend";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { DocumentBackend } from "src/app/backend/api/document.backend";
import { DomainBackend } from "src/app/backend/api/domain.backend";
import { DrugBackend } from "src/app/backend/api/drug.backend";
import { ExportBackend } from "src/app/backend/api/export.backend";
import { FiscalProfileService } from "src/app/services/fiscal-profile.service";
import { HealthRecordBackend } from "src/app/backend/api/health-record.backend";
import { ImportBackend } from "src/app/backend/api/import.backend";
import { InstallationBackend } from "src/app/backend/api/installation.backend";
import { IntegrationBackend } from "src/app/backend/api/integration.backend";
import { InvoiceBackend } from "src/app/backend/api/invoice.backend";
import { LetterheadBackend } from "src/app/backend/api/letterhead/letterhead.backend";
import { MedicalServiceBackend } from "src/app/backend/api/medical-service/medical-service.backend";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { OrderBackend } from "src/app/backend/api/order.backend";
import { OrderServiceBackend } from "src/app/backend/api/order-service.backend";
import { PackagingBackend } from "src/app/backend/api/packaging.backend";
import { PassiveInvoiceBackend } from "src/app/backend/api/passive-invoice.backend";
import { PaymentMethodBackend } from "src/app/backend/api/payment-method.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { PhysicianBackend } from "src/app/backend/api/physician.backend";
import { PrescriptionBackend } from "src/app/backend/api/prescription.backend";
import { ProductBackend } from "src/app/backend/api/product.backend";
import { QuoteBackend } from "src/app/backend/api/quote.backend";
import { QuoteTemplateBackend } from "src/app/backend/api/quote-template.backend";
import { RateTableBackend } from "src/app/backend/api/rate-table/rate-table.backend";
import { RecoveryBackend } from "src/app/backend/api/recovery.backend";
import { ReminderBackend } from "src/app/backend/api/reminder.backend";
import { RequestBackend } from "src/app/modules/bootstrap/rocket-ui/backend/api/request/request.backend";
import { RoleBackend } from "src/app/backend/api/role.backend";
import { RoomBackend } from "src/app/backend/api/room.backend";
import { SegmentationBackend } from "src/app/backend/api/segmentation.backend";
import { SoapTemplateBackend } from "src/app/backend/api/soap-template.backend";
import { SurveyBackend } from "src/app/backend/api/survey.backend";
import { TagBackend } from "src/app/backend/api/tag/tag.backend";
import { TaskBackend } from "src/app/backend/api/task.backend";
import { TemplateEmailBackend } from "src/app/backend/api/template-email/template-email.backend";
import { TenantBackend } from "src/app/backend/api/tenant.backend";
import { TicketBackend } from "src/app/backend/api/ticket.backend";
import { UserBackend } from "src/app/backend/api/user.backend";
import { VaccineBackend } from "src/app/backend/api/vaccine.backend";
import { WarehouseBackend } from "src/app/backend/api/warehouse.backend";
import { WorklistBackend } from "src/app/modules/bootstrap/rocket-ui/backend/api/request/worklist.backend";
import { WorkOrderBackend } from "src/app/backend/api/work-order/work-order.backend";
import { BookBackend } from "src/app/backend/api/book.backend";
import { StudyBackend } from "src/app/modules/bootstrap/rocket-ui/backend/api/study/study.backend";
import {
  BrokerEventLoaderResource,
  BrokerEventResolverConfig
} from "src/app/sse/broker-event.types";
import { SseEvents } from "src/app/sse/sse-events";
import { map } from "rxjs/operators";
import { of } from "rxjs";
import { MessageBackend } from "src/app/backend/api/message.backend";

export const BROKER_EVENT_LOADERS: Array<
  [SseEvents[], BrokerEventResolverConfig]
> = [
  [
    [
      SseEvents.DIAGNOSTIC_REPORT_CREATED,
      SseEvents.DIAGNOSTIC_REPORT_REJECTED,
      SseEvents.DIAGNOSTIC_STUDY_ARCHIVED,
      SseEvents.DIAGNOSTIC_STUDY_ARCHIVING_REQUESTED,
      SseEvents.DIAGNOSTIC_STUDY_ASSIGNED,
      SseEvents.DIAGNOSTIC_STUDY_CREATED,
      SseEvents.DIAGNOSTIC_STUDY_DELETED,
      SseEvents.DIAGNOSTIC_STUDY_DELIVERED,
      SseEvents.DIAGNOSTIC_STUDY_IMPORTED,
      SseEvents.DIAGNOSTIC_STUDY_REJECTED,
      SseEvents.DIAGNOSTIC_STUDY_REPORTED,
      SseEvents.DIAGNOSTIC_STUDY_RESTORED,
      SseEvents.DIAGNOSTIC_STUDY_SHARED,
      SseEvents.DIAGNOSTIC_STUDY_SHARING_FORCED,
      SseEvents.DIAGNOSTIC_STUDY_UNSHARED,
      SseEvents.DIAGNOSTIC_STUDY_UPDATED
    ],
    {
      roles: [{ subject: "study", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(StudyBackend).loadStudyById(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(StudyBackend).loadStudyByIdLight(resource)
    }
  ],
  [
    [
      SseEvents.DIAGNOSTIC_REQUEST_ACCEPTED,
      SseEvents.DIAGNOSTIC_REQUEST_CREATED,
      SseEvents.DIAGNOSTIC_REQUEST_DELETED,
      SseEvents.DIAGNOSTIC_REQUEST_REJECTED,
      SseEvents.DIAGNOSTIC_REQUEST_UPDATED
    ],
    {
      roles: [{ subject: "request", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(RequestBackend).loadRequestById(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(RequestBackend).loadRequestByIdLight(resource)
    }
  ],
  [
    [
      SseEvents.DIAGNOSTIC_WORKLIST_CREATED,
      SseEvents.DIAGNOSTIC_WORKLIST_UPDATED
    ],
    {
      roles: [{ subject: "worklist", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(WorklistBackend).loadById(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(WorklistBackend).loadByIdLight(resource)
    }
  ],
  [
    [
      SseEvents.DIAGNOSTIC_ATTACHMENT_CREATED,
      SseEvents.DIAGNOSTIC_ATTACHMENT_DELETED,
      SseEvents.DIAGNOSTIC_ATTACHMENT_UPDATED,
      SseEvents.DIAGNOSTIC_ATTACHMENT_UPLOADED
    ],
    {
      roles: [{ subject: "attachment", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(AttachmentBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(AttachmentBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.DIAGNOSTIC_LETTERHEAD_CREATED,
      SseEvents.DIAGNOSTIC_LETTERHEAD_DELETED,
      SseEvents.DIAGNOSTIC_LETTERHEAD_UPDATED
    ],
    {
      roles: [{ subject: "letterhead", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(LetterheadBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(LetterheadBackend).loadLight(resource)
    }
  ],
  [
    [SseEvents.DIAGNOSTIC_CONFIGURATION_UPDATED],
    {
      roles: [],
      resolver: () => inject(ConfigurationBackend).load()
    }
  ],
  [
    [SseEvents.DIAGNOSTIC_ARCHIVE_UPDATED],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [SseEvents.DIAGNOSTIC_BACKUP_CREATED],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.DIRECTORY_CUSTOMER_CREATED,
      SseEvents.DIRECTORY_CUSTOMER_DELETED,
      SseEvents.DIRECTORY_CUSTOMER_UPDATED
    ],
    {
      roles: [{ subject: "customer", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(CustomerBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(CustomerBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.DIRECTORY_PATIENT_CREATED,
      SseEvents.DIRECTORY_PATIENT_DECEASED,
      SseEvents.DIRECTORY_PATIENT_DELETED,
      SseEvents.DIRECTORY_PATIENT_MERGED,
      SseEvents.DIRECTORY_PATIENT_RESURRECTED,
      SseEvents.DIRECTORY_PATIENT_UPDATED,
      SseEvents.DIRECTORY_PATIENT_WEIGHING
    ],
    {
      roles: [{ subject: "patient", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(PetBackend).load({ id: resource }),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(PetBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.DIRECTORY_PHYSICIAN_CREATED,
      SseEvents.DIRECTORY_PHYSICIAN_DELETED,
      SseEvents.DIRECTORY_PHYSICIAN_DROP_PAGE_DISABLED,
      SseEvents.DIRECTORY_PHYSICIAN_DROP_PAGE_ENABLED,
      SseEvents.DIRECTORY_PHYSICIAN_UPDATED
    ],
    {
      roles: [{ subject: "physician", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(PhysicianBackend).loadLight(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(PhysicianBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.DIRECTORY_SUPPLIER_CREATED,
      SseEvents.DIRECTORY_SUPPLIER_DELETED,
      SseEvents.DIRECTORY_SUPPLIER_UPDATED
    ],
    {
      roles: [{ subject: "supplier", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(WarehouseBackend).loadSupplier(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(WarehouseBackend).loadSupplierLight(resource)
    }
  ],
  [
    [
      SseEvents.DIRECTORY_ATTACHMENT_CREATED,
      SseEvents.DIRECTORY_ATTACHMENT_UPLOADED,
      SseEvents.DIRECTORY_ATTACHMENT_DELETED
    ],
    {
      roles: [{ subject: "attachment", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        of({ id: resource }),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        of({ id: resource })
    }
  ],
  [
    [SseEvents.DIRECTORY_ATTACHMENT_UPDATED],
    {
      roles: [{ subject: "attachment", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(AttachmentDirectoryBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(AttachmentDirectoryBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.ORGANIZATION_ACCOUNT_CREATED,
      SseEvents.ORGANIZATION_ACCOUNT_DELETED,
      SseEvents.ORGANIZATION_ACCOUNT_UPDATED
    ],
    {
      roles: [],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(AccountBackend).load(resource),
      resolverLight: () => of(null)
    }
  ],
  [
    [
      SseEvents.ORGANIZATION_TENANT_CREATED,
      SseEvents.ORGANIZATION_TENANT_DELETED,
      SseEvents.ORGANIZATION_TENANT_UPDATED
    ],
    {
      roles: [],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(TenantBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(TenantBackend).loadPublic(resource)
    }
  ],
  [
    [
      SseEvents.ORGANIZATION_DOMAIN_CREATED,
      SseEvents.ORGANIZATION_DOMAIN_DELETED,
      SseEvents.ORGANIZATION_DOMAIN_UPDATED
    ],
    {
      roles: [],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(DomainBackend).load(resource),
      resolverLight: () => of(null)
    }
  ],
  [
    [
      SseEvents.CALENDAR_APPOINTMENT_CANCELED,
      SseEvents.CALENDAR_APPOINTMENT_CREATED,
      SseEvents.CALENDAR_APPOINTMENT_DELETED,
      SseEvents.CALENDAR_APPOINTMENT_NOSHOWED,
      SseEvents.CALENDAR_APPOINTMENT_RESCHEDULED,
      SseEvents.CALENDAR_APPOINTMENT_UPDATED
    ],
    {
      roles: [{ subject: "appointment", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(AppointmentBackend).load({ id: resource }),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(AppointmentBackend).loadPublic(resource)
    }
  ],
  [
    [
      SseEvents.CALENDAR_APPOINTMENT_TEMPLATE_CREATED,
      SseEvents.CALENDAR_APPOINTMENT_TEMPLATE_DELETED,
      SseEvents.CALENDAR_APPOINTMENT_TEMPLATE_UPDATED
    ],
    {
      roles: [{ subject: "template", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(AppointmentTemplateBackend).loadLight(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(AppointmentTemplateBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.CALENDAR_SHIFT_ADDED,
      SseEvents.CALENDAR_SHIFT_DELETED,
      SseEvents.CALENDAR_SHIFT_UPDATED
    ],
    {
      roles: [{ subject: "schedule", action: "read" }],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.CALENDAR_ATTACHMENT_CREATED,
      SseEvents.CALENDAR_ATTACHMENT_DELETED,
      SseEvents.CALENDAR_ATTACHMENT_UPDATED,
      SseEvents.CALENDAR_ATTACHMENT_UPLOADED
    ],
    {
      roles: [{ subject: "attachment", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(AppointmentBackend).load({ id: resource }),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(AppointmentBackend).loadPublic(resource)
    }
  ],
  [
    [SseEvents.CALENDAR_CONFIGURATION_UPDATED],
    {
      roles: [],
      resolver: () => inject(ConfigurationBackend).loadCalendar()
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_TICKET_CHECKED_IN,
      SseEvents.HEALTHCARE_TICKET_CHECKED_OUT,
      SseEvents.HEALTHCARE_TICKET_COMPLETED,
      SseEvents.HEALTHCARE_TICKET_CREATED,
      SseEvents.HEALTHCARE_TICKET_DELETED,
      SseEvents.HEALTHCARE_TICKET_STARTED,
      SseEvents.HEALTHCARE_TICKET_UPDATED
    ],
    {
      roles: [{ subject: "ticket", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(TicketBackend).load({ uuid: resource }),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(TicketBackend).loadLight({ uuid: resource })
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_HEALTH_RECORD_TEMPLATE_CLONED,
      SseEvents.HEALTHCARE_HEALTH_RECORD_TEMPLATE_CREATED,
      SseEvents.HEALTHCARE_HEALTH_RECORD_TEMPLATE_DELETED,
      SseEvents.HEALTHCARE_HEALTH_RECORD_TEMPLATE_UPDATED
    ],
    {
      roles: [{ subject: "template", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(SoapTemplateBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(SoapTemplateBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_HEALTH_RECORD_COMPLETED,
      SseEvents.HEALTHCARE_HEALTH_RECORD_CREATED,
      SseEvents.HEALTHCARE_HEALTH_RECORD_DELETED,
      SseEvents.HEALTHCARE_HEALTH_RECORD_REOPENED,
      SseEvents.HEALTHCARE_HEALTH_RECORD_UPDATED
    ],
    {
      roles: [{ subject: "health-record", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(HealthRecordBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(HealthRecordBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_TASK_COMPLETED,
      SseEvents.HEALTHCARE_TASK_CREATED,
      SseEvents.HEALTHCARE_TASK_UPDATED
    ],
    {
      roles: [{ subject: "task", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(TaskBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(TaskBackend).loadLight(resource)
    }
  ],
  [
    [SseEvents.HEALTHCARE_TASK_DELETED],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_RATE_TABLE_CREATED,
      SseEvents.HEALTHCARE_RATE_TABLE_DELETED,
      SseEvents.HEALTHCARE_RATE_TABLE_UPDATED
    ],
    {
      roles: [{ subject: "rate-table", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(RateTableBackend).loadLight(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(RateTableBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_RATE_TABLE_ITEM_CREATED,
      SseEvents.HEALTHCARE_RATE_TABLE_ITEM_DELETED,
      SseEvents.HEALTHCARE_RATE_TABLE_ITEM_UPDATED
    ],
    {
      roles: [{ subject: "rate-table", action: "read" }],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_HOSPITALIZATION_CREATED,
      SseEvents.HEALTHCARE_HOSPITALIZATION_DELETED,
      SseEvents.HEALTHCARE_HOSPITALIZATION_UPDATED
    ],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_WORK_ORDER_CREATED,
      SseEvents.HEALTHCARE_WORK_ORDER_DELETED,
      SseEvents.HEALTHCARE_WORK_ORDER_RECONCILED,
      SseEvents.HEALTHCARE_WORK_ORDER_UPDATED
    ],
    {
      roles: [{ subject: "work-order", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(WorkOrderBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(WorkOrderBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_WORK_ORDER_ITEM_ADDED,
      SseEvents.HEALTHCARE_WORK_ORDER_ITEM_UPDATED,
      SseEvents.HEALTHCARE_WORK_ORDER_ITEM_DELETED
    ],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [SseEvents.HEALTHCARE_WORK_ORDER_EXAM_PARAMETER_UPDATED],
    {
      roles: [{ subject: "work-order", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(WorkOrderBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_DRUG_CREATED,
      SseEvents.HEALTHCARE_DRUG_DELETED,
      SseEvents.HEALTHCARE_DRUG_UPDATED
    ],
    {
      roles: [{ subject: "drug", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(DrugBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(DrugBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_PACKAGING_CREATED,
      SseEvents.HEALTHCARE_PACKAGING_DELETED,
      SseEvents.HEALTHCARE_PACKAGING_UPDATED
    ],
    {
      roles: [{ subject: "packaging", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(PackagingBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_PRODUCT_CREATED,
      SseEvents.HEALTHCARE_PRODUCT_DELETED,
      SseEvents.HEALTHCARE_PRODUCT_UPDATED
    ],
    {
      roles: [{ subject: "product", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(ProductBackend).load(resource, 0, undefined),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(ProductBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_MEDICAL_SERVICE_CREATED,
      SseEvents.HEALTHCARE_MEDICAL_SERVICE_DELETED,
      SseEvents.HEALTHCARE_MEDICAL_SERVICE_UPDATED
    ],
    {
      roles: [{ subject: "medical-service", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(MedicalServiceBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(MedicalServiceBackend).loadLight(resource)
    }
  ],
  [
    [SseEvents.HEALTHCARE_CONFIGURATION_HEALTHCARE_UPDATED],
    {
      roles: [],
      resolver: () => inject(ConfigurationBackend).loadHealthcare()
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_DOCUMENT_CREATED,
      SseEvents.HEALTHCARE_DOCUMENT_DELETED,
      SseEvents.HEALTHCARE_DOCUMENT_UPDATED
    ],
    {
      roles: [{ subject: "document", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(DocumentBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_PRESCRIPTION_CANCELED,
      SseEvents.HEALTHCARE_PRESCRIPTION_CREATED,
      SseEvents.HEALTHCARE_PRESCRIPTION_DELETED,
      SseEvents.HEALTHCARE_PRESCRIPTION_EMITTED,
      SseEvents.HEALTHCARE_PRESCRIPTION_UPDATED
    ],
    {
      roles: [{ subject: "prescription", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(PrescriptionBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(PrescriptionBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_PRESCRIPTION_DRUG_ADDED,
      SseEvents.HEALTHCARE_PRESCRIPTION_DRUG_DELETED,
      SseEvents.HEALTHCARE_PRESCRIPTION_DRUG_UPDATED
    ],
    {
      roles: [{ subject: "prescription", action: "read" }],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_ATTACHMENT_CREATED,
      SseEvents.HEALTHCARE_ATTACHMENT_DELETED,
      SseEvents.HEALTHCARE_ATTACHMENT_UPDATED,
      SseEvents.HEALTHCARE_ATTACHMENT_UPLOADED
    ],
    {
      roles: [{ subject: "attachment", action: "read" }],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_EXPORT_COMPLETED,
      SseEvents.HEALTHCARE_EXPORT_CREATED,
      SseEvents.HEALTHCARE_EXPORT_REJECTED
    ],
    {
      roles: [{ subject: "export", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(ExportBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_IMPORT_COMPLETED,
      SseEvents.HEALTHCARE_IMPORT_CREATED,
      SseEvents.HEALTHCARE_IMPORT_REJECTED
    ],
    {
      roles: [{ subject: "import", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(ImportBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_RECOVERY_COMPLETED,
      SseEvents.HEALTHCARE_RECOVERY_CREATED,
      SseEvents.HEALTHCARE_RECOVERY_DELETED,
      SseEvents.HEALTHCARE_RECOVERY_UPDATED
    ],
    {
      roles: [{ subject: "recovery", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(RecoveryBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_VACCINE_CREATED,
      SseEvents.HEALTHCARE_VACCINE_DELETED,
      SseEvents.HEALTHCARE_VACCINE_UPDATED
    ],
    {
      roles: [],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(VaccineBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_LETTERHEAD_CREATED,
      SseEvents.HEALTHCARE_LETTERHEAD_DELETED,
      SseEvents.HEALTHCARE_LETTERHEAD_UPDATED
    ],
    {
      roles: [{ subject: "letterhead", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(LetterheadBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_ROOM_CREATED,
      SseEvents.HEALTHCARE_ROOM_DELETED,
      SseEvents.HEALTHCARE_ROOM_UPDATED
    ],
    {
      roles: [{ subject: "room", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(RoomBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(RoomBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_ROLE_CREATED,
      SseEvents.HEALTHCARE_ROLE_DELETED,
      SseEvents.HEALTHCARE_ROLE_UPDATED
    ],
    {
      roles: [{ subject: "role", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(RoleBackend).loadLight({ id: resource })
    }
  ],
  [
    [
      SseEvents.HEALTHCARE_SURVEY_COMPLETED,
      SseEvents.HEALTHCARE_SURVEY_CREATED,
      SseEvents.HEALTHCARE_SURVEY_SENT
    ],
    {
      roles: [{ subject: "survey", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(SurveyBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.IAM_MEMBER_CREATED,
      SseEvents.IAM_MEMBER_DELETED,
      SseEvents.IAM_MEMBER_DISABLED,
      SseEvents.IAM_MEMBER_ENABLED,
      SseEvents.IAM_MEMBER_UPDATED
    ],
    {
      roles: [{ subject: "member", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(MemberBackend).loadLight(resource)
    }
  ],
  [
    [SseEvents.IAM_USER_UPDATED],
    {
      roles: [{ subject: "member", action: "read" }],
      resolver: () => inject(UserBackend).loadMe()
    }
  ],
  [
    [
      SseEvents.NOTIFICATION_MESSAGE_TEMPLATE_CREATED,
      SseEvents.NOTIFICATION_MESSAGE_TEMPLATE_DELETED,
      SseEvents.NOTIFICATION_MESSAGE_TEMPLATE_UPDATED
    ],
    {
      roles: [{ subject: "template", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(TemplateEmailBackend).load({ id: resource })
    }
  ],
  [
    [
      SseEvents.NOTIFICATION_MESSAGE_CREATED,
      SseEvents.NOTIFICATION_MESSAGE_DELETED,
      SseEvents.NOTIFICATION_MESSAGE_UPDATED
    ],
    {
      roles: [{ subject: "message", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(MessageBackend).loadById(resource)
    }
  ],
  [
    [
      SseEvents.NOTIFICATION_REMINDER_CREATED,
      SseEvents.NOTIFICATION_REMINDER_DELETED,
      SseEvents.NOTIFICATION_REMINDER_SENT,
      SseEvents.NOTIFICATION_REMINDER_UPDATED
    ],
    {
      roles: [{ subject: "reminder", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(ReminderBackend).load(resource)
    }
  ],
  [
    [SseEvents.NOTIFICATION_ALARM_CREATED],
    {
      roles: [{ subject: "notification", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(AlarmBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.NOTIFICATION_CAMPAIGN_CREATED,
      SseEvents.NOTIFICATION_CAMPAIGN_DELETED,
      SseEvents.NOTIFICATION_CAMPAIGN_DISABLED,
      SseEvents.NOTIFICATION_CAMPAIGN_ENABLED,
      SseEvents.NOTIFICATION_CAMPAIGN_SENT,
      SseEvents.NOTIFICATION_CAMPAIGN_UPDATED
    ],
    {
      roles: [{ subject: "campaign", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(CampaignBackend).loadById(resource)
    }
  ],
  [
    [
      SseEvents.NOTIFICATION_SEGMENTATION_CREATED,
      SseEvents.NOTIFICATION_SEGMENTATION_DELETED,
      SseEvents.NOTIFICATION_SEGMENTATION_UPDATED
    ],
    {
      roles: [{ subject: "segmentation", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(SegmentationBackend).loadById(resource)
    }
  ],
  [
    [SseEvents.BILLING_CONFIGURATION_BILLING_UPDATED],
    {
      roles: [],
      resolver: () => inject(ConfigurationBackend).loadBilling()
    }
  ],
  [
    [SseEvents.BILLING_CART_CREATED, SseEvents.BILLING_CART_UPDATED],
    {
      roles: [{ subject: "cart", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(CartBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.BILLING_CART_ITEM_ADDED,
      SseEvents.BILLING_CART_ITEM_DELETED,
      SseEvents.BILLING_CART_ITEM_UPDATED
    ],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.BILLING_INVOICE_CREATED,
      SseEvents.BILLING_INVOICE_DELETED,
      SseEvents.BILLING_INVOICE_EMITTED,
      SseEvents.BILLING_INVOICE_LOCKED,
      SseEvents.BILLING_INVOICE_UNLOCKED,
      SseEvents.BILLING_INVOICE_UPDATED
    ],
    {
      roles: [{ subject: "invoice", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(InvoiceBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(InvoiceBackend).loadLight(resource)
    }
  ],
  [
    [SseEvents.BILLING_INVOICE_ITEM_DELETED],
    { roles: [], resolver: () => of(null) }
  ],
  [
    [
      SseEvents.BILLING_INVOICE_PAYMENT_ADDED,
      SseEvents.BILLING_INVOICE_PAYMENT_DELETED,
      SseEvents.BILLING_INVOICE_PAYMENT_UPDATED
    ],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.BILLING_FISCAL_PROFILE_CREATED,
      SseEvents.BILLING_FISCAL_PROFILE_UPDATED
    ],
    {
      roles: [{ subject: "fiscal-profile", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(FiscalProfileService).refresh(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(FiscalProfileService)
          .refresh(resource)
          .pipe(
            map((profile) => ({
              id: profile.id,
              name: profile.name,
              default: profile.default
            }))
          )
    }
  ],
  [
    [SseEvents.BILLING_FISCAL_PROFILE_DELETED],
    {
      roles: [{ subject: "fiscal-profile", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) => {
        inject(FiscalProfileService).remove(resource);

        return of(null);
      }
    }
  ],
  [
    [
      SseEvents.BILLING_QUOTE_ACCEPTED,
      SseEvents.BILLING_QUOTE_CREATED,
      SseEvents.BILLING_QUOTE_DELETED,
      SseEvents.BILLING_QUOTE_REJECTED,
      SseEvents.BILLING_QUOTE_UNACCEPTED,
      SseEvents.BILLING_QUOTE_UNREJECTED,
      SseEvents.BILLING_QUOTE_UPDATED
    ],
    {
      roles: [{ subject: "quote", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(QuoteBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(QuoteBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.BILLING_QUOTE_TEMPLATE_CREATED,
      SseEvents.BILLING_QUOTE_TEMPLATE_DELETED,
      SseEvents.BILLING_QUOTE_TEMPLATE_UPDATED
    ],
    {
      roles: [{ subject: "template", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(QuoteTemplateBackend).load(resource),
      resolverLight: ({ resource }: BrokerEventLoaderResource) =>
        inject(QuoteTemplateBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.BILLING_QUOTE_TEMPLATE_ITEM_ADDED,
      SseEvents.BILLING_QUOTE_TEMPLATE_ITEM_DELETED,
      SseEvents.BILLING_QUOTE_TEMPLATE_ITEM_UPDATED
    ],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [SseEvents.BILLING_BOOK_CREATED],
    {
      roles: [{ subject: "book", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(BookBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.BILLING_BOOK_ITEM_ADDED,
      SseEvents.BILLING_BOOK_ITEM_DELETED,
      SseEvents.BILLING_BOOK_ITEM_EXECUTION_DATE_SET,
      SseEvents.BILLING_BOOK_ITEM_EXECUTORS_SET,
      SseEvents.BILLING_BOOK_ITEM_UPDATED
    ],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.BILLING_ATTACHMENT_CREATED,
      SseEvents.BILLING_ATTACHMENT_DELETED,
      SseEvents.BILLING_ATTACHMENT_SIGNED,
      SseEvents.BILLING_ATTACHMENT_UPDATED,
      SseEvents.BILLING_ATTACHMENT_UPLOADED
    ],
    {
      roles: [{ subject: "attachment", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(AttachmentBillingBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.BILLING_SENDING_CREATED,
      SseEvents.BILLING_SENDING_DISCARDED,
      SseEvents.BILLING_SENDING_SENT,
      SseEvents.BILLING_SENDING_UPDATED,
      SseEvents.BILLING_SENDING_REJECTED
    ],
    {
      roles: [{ subject: "sending", action: "read" }],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.BILLING_SENDING_OUTCOME_DETAIL_UPLOADED,
      SseEvents.BILLING_SENDING_RECEIPT_UPLOADED
    ],
    {
      roles: [{ subject: "sending", action: "read" }],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.BILLING_PAYMENT_METHOD_CREATED,
      SseEvents.BILLING_PAYMENT_METHOD_DELETED,
      SseEvents.BILLING_PAYMENT_METHOD_DISABLED,
      SseEvents.BILLING_PAYMENT_METHOD_UPDATED
    ],
    {
      roles: [{ subject: "payment-method", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(PaymentMethodBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.BILLING_BANK_ACCOUNTS_CREATED,
      SseEvents.BILLING_BANK_ACCOUNTS_DELETED,
      SseEvents.BILLING_BANK_ACCOUNTS_UPDATED
    ],
    {
      roles: [{ subject: "bank-account", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(BankAccountBackend).loadLight(resource)
    }
  ],
  [
    [SseEvents.BILLING_PASSIVE_INVOICE_CREATED],
    {
      roles: [{ subject: "invoice", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(PassiveInvoiceBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.BILLING_PASSIVE_INVOICE_PAYMENT_ADDED,
      SseEvents.BILLING_PASSIVE_INVOICE_PAYMENT_DELETED,
      SseEvents.BILLING_PASSIVE_INVOICE_PAYMENT_UPDATED
    ],
    {
      roles: [],
      resolver: () => of(null)
    }
  ],
  [
    [SseEvents.ANALYTICS_SALE_CREATED],
    {
      roles: [{ subject: "analytics", action: "read" }],
      resolver: () => of(null)
    }
  ],
  [
    [
      SseEvents.TAG_TAG_CREATED,
      SseEvents.TAG_TAG_DELETED,
      SseEvents.TAG_TAG_UPDATED
    ],
    {
      roles: [{ subject: "tag", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(TagBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.INTEGRATOR_INSTALLATION_CREATED,
      SseEvents.INTEGRATOR_INSTALLATION_DELETED,
      SseEvents.INTEGRATOR_INSTALLATION_DISABLED,
      SseEvents.INTEGRATOR_INSTALLATION_ENABLED,
      SseEvents.INTEGRATOR_INSTALLATION_UPDATED
    ],
    {
      roles: [{ subject: "installation", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(InstallationBackend).loadById(resource)
    }
  ],
  [
    [
      SseEvents.INTEGRATOR_CONNECTION_CREATED,
      SseEvents.INTEGRATOR_CONNECTION_DELETED,
      SseEvents.INTEGRATOR_CONNECTION_SHARED,
      SseEvents.INTEGRATOR_CONNECTION_UNSHARED,
      SseEvents.INTEGRATOR_CONNECTION_UPDATED
    ],
    {
      roles: [{ subject: "integration", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(IntegrationBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.STORE_SERVICE_CREATED,
      SseEvents.STORE_SERVICE_DELETED,
      SseEvents.STORE_SERVICE_UPDATED
    ],
    {
      roles: [{ subject: "medical-service", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(OrderServiceBackend).load(resource)
    }
  ],
  [
    [
      SseEvents.STORE_ORDER_CREATED,
      SseEvents.STORE_ORDER_DELETED,
      SseEvents.STORE_ORDER_UPDATED
    ],
    {
      roles: [],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(OrderBackend).loadLight(resource)
    }
  ],
  [
    [
      SseEvents.WAREHOUSE_GOOD_CREATED,
      SseEvents.WAREHOUSE_GOOD_DELETED,
      SseEvents.WAREHOUSE_GOOD_LOADED,
      SseEvents.WAREHOUSE_GOOD_UNLOADED,
      SseEvents.WAREHOUSE_GOOD_UPDATED
    ],
    {
      roles: [{ subject: "good", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(WarehouseBackend).loadGoodLight(resource)
    }
  ],
  [
    [
      SseEvents.WAREHOUSE_STORAGE_CREATED,
      SseEvents.WAREHOUSE_STORAGE_DELETED,
      SseEvents.WAREHOUSE_STORAGE_UPDATED
    ],
    {
      roles: [{ subject: "storage", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(WarehouseBackend).loadStorageLight(resource)
    }
  ],
  [
    [
      SseEvents.WAREHOUSE_MOVEMENT_CREATED,
      SseEvents.WAREHOUSE_MOVEMENT_DELETED,
      SseEvents.WAREHOUSE_MOVEMENT_UPDATED
    ],
    {
      roles: [{ subject: "movement", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(WarehouseBackend).loadMovementLight(resource)
    }
  ],
  [
    [
      SseEvents.WAREHOUSE_BUSINESS_UNIT_CREATED,
      SseEvents.WAREHOUSE_BUSINESS_UNIT_DELETED,
      SseEvents.WAREHOUSE_BUSINESS_UNIT_UPDATED
    ],
    {
      roles: [{ subject: "business-unit", action: "read" }],
      resolver: ({ resource }: BrokerEventLoaderResource) =>
        inject(WarehouseBackend).loadBusinessUnitLight(resource)
    }
  ]
];
